import { ClockCircleOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Flex, Typography, theme } from 'antd';
import type { FC } from 'react';
import { Price } from '../../../components/Price.tsx';
import { TestTubeColor } from '../../../components/TestTubeColor.tsx';
import type { SelectedParameter } from '../../../hooks/store/useAppStore.ts';
import { testTubeColorProperties } from '../../../utils/enumHelpers.ts';
import { Billing } from './Billing.tsx';

export const ParamEntry: FC<{
  param: SelectedParameter;
  swap: boolean;
  showLab: boolean;
  showPrice: boolean;
  onInfo: (param: SelectedParameter) => void;
}> = ({ param, swap, showLab, showPrice, onInfo }) => {
  const { token } = theme.useToken();

  const ellipsis = css`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  `;

  // Kurzname is the identifier — always monospace, wherever `swap` places it.
  const mono = css`
    font-family: ${token.fontFamilyCode};
  `;

  return (
    <Flex
      gap={token.paddingSM}
      onClick={() => onInfo(param)}
      className={css`
        border: 1px solid ${token.Layout?.bodyBg};
        border-radius: ${token.borderRadiusSM}px;
        padding: ${token.paddingXXS}px;
        background-color: ${token.Layout?.bodyBg};
        cursor: pointer;

        :hover {
          border-color: ${token.colorPrimary};
        }

        transition: border-color 200ms linear;
      `}
    >
      <Flex
        vertical
        flex="max-content"
        className={css`
          min-width: 0;
        `}
      >
        <span className={cx(ellipsis, !swap && mono)} style={{ fontWeight: 500 }}>
          {swap ? param.longName : param.shortName}
        </span>
        <Typography.Text
          type="secondary"
          className={cx(
            css`
              ${ellipsis};
              line-height: 12px;
            `,
            swap && mono
          )}
        >
          <small>{swap ? param.shortName : param.longName}</small>
        </Typography.Text>
      </Flex>
      <Flex vertical>
        <Flex gap={4} align="center" justify="end">
          <Billing billingInfo={param.billingInfo} />
          {param.specimens
            .flatMap(it => it.testTubes)
            .map((tt, i) => (
              <TestTubeColor key={`${tt.id}_${i}`} withText={false} properties={testTubeColorProperties(tt.color)} />
            ))}
        </Flex>
        <Typography.Text
          type="secondary"
          className={css`
            text-align: right;
            line-height: 12px;
          `}
        >
          <small>
            {showPrice ? <Price value={param.billingInfo.price} /> : ''} {showLab ? param.lab.shortName : ''}
          </small>
          {param.acute ? <ClockCircleOutlined className={css(`font-size: 12px; padding-left: 4px`)} /> : null}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};
